import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Select,
  TextField,
} from "@mui/material";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { exportPayrollToPDF } from "../../utils/exportPayroll";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";

export default function PayrollsTable({ selectedBranch = "" }) {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();
  const allEmployeesOption = React.useMemo(
    () => ({ id: "", name: t("ReportIndex.all_employees", "All Employees") }),
    [t]
  );

  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [selectedEmployee, setSelectedEmployee] = React.useState("");
  const [employeeSearchTerm, setEmployeeSearchTerm] = React.useState("");
  const [selectedEmployeeOption, setSelectedEmployeeOption] = React.useState(
    allEmployeesOption
  );
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const employeeSearchFilter = React.useMemo(() => {
    const trimmedSearch = employeeSearchTerm.trim();

    if (!trimmedSearch || selectedEmployee) return "";

    if (
      trimmedSearch.toLowerCase() === allEmployeesOption.name.toLowerCase()
    ) {
      return "";
    }

    return trimmedSearch;
  }, [allEmployeesOption.name, employeeSearchTerm, selectedEmployee]);

  // Build query string
  const queryString = React.useMemo(() => {
    const params = [];
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    if (selectedBranch) params.push(`shop=${selectedBranch}`);
    if (selectedEmployee) params.push(`employee=${selectedEmployee}`);
    if (employeeSearchFilter) {
      params.push(`search=${encodeURIComponent(employeeSearchFilter)}`);
    }
    if (rowsPerPage === -1) {
      params.push("page=1");
      params.push("page_size=all");
    } else {
      params.push(`page=${page}`);
      params.push(`page_size=${rowsPerPage}`);
    }
    return params.join("&");
  }, [
    employeeSearchFilter,
    endDate,
    page,
    rowsPerPage,
    selectedBranch,
    selectedEmployee,
    startDate,
  ]);

  const {
    data: payrollsData,
    isLoading,
    isError,
  } = useFetchObjects(
    [
      "payrolls",
      queryString,
      selectedEmployee,
      employeeSearchFilter,
      page,
      rowsPerPage,
    ],
    `shop/payrolls/?${queryString}`,
    token
  );

  React.useEffect(() => {
    setPage(1);
  }, [startDate, endDate, selectedEmployee, employeeSearchFilter]);

  React.useEffect(() => {
    if (!selectedEmployee && !employeeSearchTerm.trim()) {
      setSelectedEmployeeOption(allEmployeesOption);
    }
  }, [
    allEmployeesOption,
    employeeSearchTerm,
    selectedEmployee,
  ]);

  // Fetch employees for filter dropdown
  const { data: employeesData, isLoading: employeesLoading } = useFetchObjects(
    ["employees", "payroll-report-filter", employeeSearchTerm],
    `shop/employees/?search=${encodeURIComponent(
      employeeSearchTerm.trim()
    )}&page_size=20`,
    token
  );

  const employees = Array.isArray(employeesData)
    ? employeesData
    : employeesData?.results || [];

  const employeeOptions = React.useMemo(() => {
    const options = [allEmployeesOption, ...employees];

    if (!selectedEmployeeOption) {
      return options;
    }

    const alreadyIncluded = options.some(
      (employee) =>
        String(employee?.id ?? "") === String(selectedEmployeeOption?.id ?? "")
    );

    return alreadyIncluded
      ? options
      : [selectedEmployeeOption, ...options];
  }, [allEmployeesOption, employees, selectedEmployeeOption]);

  const payrolls = payrollsData?.results || payrollsData || [];
  const totalCount = payrollsData?.count || payrolls.length;

  // Calculate summary totals
  const summaryTotals = React.useMemo(() => {
    return payrolls.reduce((acc, payroll) => {
      const salary = parseFloat(payroll.employee_details?.salary) || 0;
      const amount = parseFloat(payroll.amount) || 0;
      const remaining = salary - amount;
      
      return {
        totalSalary: acc.totalSalary + salary,
        totalAmount: acc.totalAmount + amount,
        totalRemaining: acc.totalRemaining + remaining
      };
    }, { totalSalary: 0, totalAmount: 0, totalRemaining: 0 });
  }, [payrolls]);

  const open = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleExportClick = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    const base = `payrolls_${today}`;

    if (type === "pdf") exportPayrollToPDF(payrolls, `${base}.pdf`);
    handleClose();
  };

  if (isLoading)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          width: "100%",
          alignItems: "center",
          gap: 1,
        }}
      >
        <LinearProgress sx={{ width: "90%" }} />
        <Typography>{t("ReportIndex.loading")}</Typography>
      </Box>
    );

  if (isError)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
          flexDirection: "column",
          alignItems: "center",
          px: 2,
          gap: 1,
        }}
      >
        <Typography variant="h6" color="error">
          {t("ReportIndex.error")}{" "}
          <RefreshIcon sx={{ cursor: "pointer", verticalAlign: "middle" }} />
        </Typography>
      </Box>
    );

  return (
    <>
      {/* Filters */}
      <Box sx={{ flexGrow: 1, mb: 5, m: "15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <DatePicker
                  label={t("ReportIndex.datepicker_start")}
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label={t("ReportIndex.datepicker_end")}
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <Autocomplete
                  fullWidth
                  options={employeeOptions}
                  value={selectedEmployeeOption}
                  inputValue={employeeSearchTerm}
                  onChange={(event, newValue) => {
                    const nextOption =
                      newValue && String(newValue.id ?? "") === ""
                        ? allEmployeesOption
                        : newValue || allEmployeesOption;

                    setSelectedEmployeeOption(nextOption);
                    setSelectedEmployee(
                      nextOption?.id ? String(nextOption.id) : ""
                    );
                    setEmployeeSearchTerm(nextOption?.name || "");
                    setPage(1);
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      setEmployeeSearchTerm(newInputValue);
                      if (
                        selectedEmployee ||
                        selectedEmployeeOption?.id === "" ||
                        selectedEmployeeOption?.name !== newInputValue
                      ) {
                        setSelectedEmployee("");
                        setSelectedEmployeeOption(null);
                        setPage(1);
                      }
                    }

                    if (reason === "clear") {
                      setEmployeeSearchTerm("");
                      setSelectedEmployee("");
                      setSelectedEmployeeOption(allEmployeesOption);
                      setPage(1);
                    }
                  }}
                  loading={employeesLoading}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id ?? "") === String(value?.id ?? "")
                  }
                  clearOnBlur={false}
                  openOnFocus
                  autoHighlight
                  noOptionsText={t(
                    "payroll.noEmployeesFound",
                    "No employees found"
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("ReportIndex.payroll_table_employeeName", "Employee")}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedEmployee("");
                    setEmployeeSearchTerm("");
                    setSelectedEmployeeOption(allEmployeesOption);
                    setPage(1);
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                  }}
                >
                  {t("ReportIndex.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-button"
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    fullWidth
                    sx={{
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                      flexShrink: 0,
                      height: 55,
                    }}
                  >
                    {t("ReportIndex.btn_export")}
                  </Button>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                      "aria-labelledby": "export-button",
                    }}
                  >
                    <MenuItem onClick={() => handleExportClick("excel")}>
                      {t("ReportIndex.export_excel")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("pdf")}>
                      {t("ReportIndex.export_pdf")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("none")}>
                      {t("ReportIndex.export_none")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, ml: 2 }}>
        <Typography>{t("reports.rowsPerPage")}</Typography>
        <FormControl size="small">
          <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
            <MenuItem value={-1}>{t("reports.all")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Divider />

      {/* Payrolls Table */}
      <TableContainer component={Paper}>
        <Table aria-label={t("ReportIndex.payroll_tableTitle")}>
          <TableHead>
            <TableRow>
              <TableCell colSpan={9} align="center">
                <Typography variant="h4">
                  {t("ReportIndex.payroll_tableTitle")}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("ReportIndex.payroll_table_id")}</TableCell>
              <TableCell>
                {t("ReportIndex.payroll_table_employeeName")}
              </TableCell>
              <TableCell>{t("ReportIndex.payroll_table_contact")}</TableCell>
              <TableCell>{t("ReportIndex.payroll_table_salary")}</TableCell>
              <TableCell>{t("ReportIndex.payroll_table_year")}</TableCell>
              <TableCell>{t("ReportIndex.payroll_table_month")}</TableCell>
              <TableCell>{t("ReportIndex.payroll_table_amount")}</TableCell>
              <TableCell>{t("Remaining Amount")}</TableCell>
              <TableCell>
                {t("ReportIndex.payroll_table_paymentDate")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payrolls.map((payroll) => {
              const salary = parseFloat(payroll.employee_details?.salary) || 0;
              const amount = parseFloat(payroll.amount) || 0;
              const remaining = salary - amount;
              
              return (
                <TableRow key={payroll.id}>
                  <TableCell>{payroll.id}</TableCell>
                  <TableCell>{payroll.employee_details?.name || "-"}</TableCell>
                  <TableCell>
                    {payroll.employee_details?.contact || "-"}
                  </TableCell>
                  <TableCell>
                    {payroll.employee_details?.salary || "-"}{" "}
                    {payroll.currency_details?.title}
                  </TableCell>
                  <TableCell>{payroll.year || "-"}</TableCell>
                  <TableCell>{payroll.month || "-"}</TableCell>
                  <TableCell>{payroll.amount}</TableCell>
                  <TableCell>
                    {remaining.toFixed(2)} {payroll.currency_details?.title}
                  </TableCell>
                  <TableCell>
                    {payroll.payment_date?.slice(0, 10) || "-"}
                  </TableCell>
                </TableRow>
              );
            })}
            {/* Summary Row */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>{t('Summary')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalSalary.toFixed(2)}
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalAmount.toFixed(2)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalRemaining.toFixed(2)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {rowsPerPage !== -1 && (
        <Grid container justifyContent="center" alignItems="center" padding="10px">
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page}
            onChange={(event, newPage) => setPage(newPage)}
            variant="outlined"
            color="primary"
          />
        </Grid>
      )}
    </>
  );
}
